import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Alert,
} from 'antd';
import './settings-modal.scss';
import { captureAnalytics } from '../../actions/preload';

const { Option } = Select;

// Condition kinds a rule can carry (besides labels, which have their own
// multi-select + any/all mode). Mirrors TeamRoutingCondition::VALID_KINDS.
const SCALAR_KINDS = ['document_category', 'fax_line', 'sender', 'addressee'];

class AddTeamRoutingRuleModal extends Component {
  constructor(props) {
    super(props);
    const { editingRule } = props;

    const existingLabelIds = editingRule?.labels?.length > 0
      ? editingRule.labels.map((l) => l.id)
      : [];

    this.state = {
      name: editingRule?.name || '',
      teamSlug: editingRule?.team_slug || undefined,
      documentCategoryId: editingRule?.document_category?.id || undefined,
      labelIds: existingLabelIds,
      labelMatchMode: editingRule?.label_match_mode || 'any', // 'any' (OR) or 'all' (AND)
      faxLineId: editingRule?.fax_line?.id || undefined,
      sender: editingRule?.sender || '',
      addresseeId: editingRule?.addressee?.id || undefined,
      active: editingRule?.active ?? true,
      teamTouched: false,
    };
  }

  handleSubmit = () => {
    const {
      name,
      teamSlug,
      labelMatchMode,
      labelIds,
      active,
    } = this.state;
    const { onSave, editingRule } = this.props;

    const validLabelIds = labelIds.filter((id) => id != null);
    const conditionAttributes = editingRule
      ? this.buildUpdateConditions(validLabelIds)
      : this.buildCreateConditions(validLabelIds);

    const scalarConditionCount = SCALAR_KINDS
      .filter((kind) => !!this.scalarValues()[kind]).length;
    captureAnalytics('team_routing_rule_saved', {
      condition_count: validLabelIds.length + scalarConditionCount,
      is_edit: !!editingRule,
    });

    onSave({
      name: name || null,
      team_slug: teamSlug,
      active,
      label_match_mode: validLabelIds.length > 1 ? labelMatchMode : 'any',
      team_routing_conditions_attributes: conditionAttributes,
    });
  };

  // Current scalar condition values keyed by kind ('' / null = no condition).
  scalarValues = () => {
    const {
      documentCategoryId, faxLineId, sender, addresseeId,
    } = this.state;
    return {
      document_category: documentCategoryId,
      fax_line: faxLineId,
      sender: sender ? sender.trim() : '',
      addressee: addresseeId,
    };
  };

  buildCreateConditions = (labelIds) => {
    const conditions = [];
    const values = this.scalarValues();

    SCALAR_KINDS.forEach((kind) => {
      if (values[kind]) {
        conditions.push({ kind, operator: 'equals', value: values[kind] });
      }
    });

    labelIds.forEach((labelId) => {
      conditions.push({ kind: 'label', operator: 'equals', value: labelId });
    });

    return conditions;
  };

  buildUpdateConditions = (labelIds) => {
    const { editingRule } = this.props;
    const existingConditions = editingRule.conditions || [];
    const conditions = [];
    const values = this.scalarValues();

    // Scalar kinds: keep/rewrite the existing condition, destroy it when
    // cleared, or create a new one when set for the first time.
    SCALAR_KINDS.forEach((kind) => {
      const existing = existingConditions.find((c) => c.kind === kind);
      if (values[kind]) {
        conditions.push({
          id: existing?.id,
          kind,
          operator: 'equals',
          value: values[kind],
          _destroy: false,
        });
      } else if (existing) {
        conditions.push({ id: existing.id, _destroy: true });
      }
    });

    // Labels: match new label IDs with existing conditions where possible.
    const existingLabelConditions = existingConditions.filter((c) => c.kind === 'label');
    const usedExistingIds = new Set();

    labelIds.forEach((labelId, index) => {
      const existing = existingLabelConditions[index];
      if (existing) {
        usedExistingIds.add(existing.id);
        conditions.push({
          id: existing.id,
          kind: 'label',
          operator: 'equals',
          value: labelId,
          _destroy: false,
        });
      } else {
        conditions.push({ kind: 'label', operator: 'equals', value: labelId });
      }
    });

    existingLabelConditions.forEach((c) => {
      if (!usedExistingIds.has(c.id)) {
        conditions.push({ id: c.id, _destroy: true });
      }
    });

    return conditions;
  };

  hasAnyCondition = () => {
    const { labelIds } = this.state;
    const values = this.scalarValues();
    return labelIds.length > 0 || SCALAR_KINDS.some((kind) => !!values[kind]);
  };

  labelsHelpText = () => {
    const { labelIds, labelMatchMode } = this.state;
    if (labelIds.length <= 1) return '';
    if (labelMatchMode === 'all') {
      return `Rule will match documents with ALL of these ${labelIds.length} labels`;
    }
    return `Rule will match documents with ANY of these ${labelIds.length} labels`;
  };

  isFormValid = () => {
    const { teamSlug } = this.state;
    // Only the target team is required — a rule with no conditions is a
    // catch-all fallback (place it at the lowest priority).
    return !!teamSlug;
  };

  render() {
    const {
      visible,
      onClose,
      teams,
      categories,
      labels,
      faxLines,
      addresseeOptions,
      onSearchAddressees,
      editingRule,
      isLoading,
    } = this.props;

    const {
      name,
      teamSlug,
      documentCategoryId,
      labelIds,
      labelMatchMode,
      faxLineId,
      sender,
      addresseeId,
      active,
      teamTouched,
    } = this.state;

    const showTeamError = teamTouched && !teamSlug;
    const editingAddressee = editingRule?.addressee;

    return (
      <Modal
        wrapClassName="settings-modal-wrap"
        visible={visible}
        title={editingRule ? 'Edit Team Routing Rule' : 'Add Team Routing Rule'}
        onCancel={onClose}
        onOk={this.handleSubmit}
        okText={editingRule ? 'Save' : 'Create'}
        okButtonProps={{
          disabled: !this.isFormValid(),
          loading: isLoading,
        }}
        width={520}
      >
        <Alert
          type="info"
          showIcon
          message="When are these rules used?"
          description={(
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              <li>Rules run automatically after an inbound document is labeled and categorized</li>
              <li>The first matching rule (top to bottom) tags the document with the team</li>
              <li>All chosen conditions must match; a rule with no conditions catches everything</li>
            </ul>
          )}
          style={{ marginBottom: 16 }}
        />

        {!this.hasAnyCondition() && (
          <Alert
            type="warning"
            showIcon
            message="No conditions: this rule will match every inbound document. Use it as a low-priority fallback."
            style={{ marginBottom: 16 }}
          />
        )}

        <Form layout="vertical">
          <Form.Item
            label="Name"
            help="Optional - helps distinguish similar rules"
          >
            <Input
              value={name}
              onChange={(e) => this.setState({ name: e.target.value })}
              placeholder="e.g., Referrals to Intake Team"
            />
          </Form.Item>

          <Form.Item
            label="Route to Team"
            required
            validateStatus={showTeamError ? 'error' : ''}
            help={showTeamError ? 'Please select a target team' : ''}
          >
            <Select
              value={teamSlug}
              onChange={(value) => this.setState({ teamSlug: value, teamTouched: true })}
              placeholder="Select a team"
              showSearch
              optionFilterProp="children"
            >
              {teams.map((team) => (
                <Option key={team.id} value={team.id}>
                  {team.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Document Category">
            <Select
              value={documentCategoryId}
              onChange={(value) => this.setState({ documentCategoryId: value || undefined })}
              placeholder="Any category"
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.emr_category}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Labels"
            help={this.labelsHelpText()}
          >
            <Select
              mode="multiple"
              value={labelIds}
              onChange={(value) => {
                let newLabelIds = [];
                if (Array.isArray(value)) {
                  newLabelIds = value.filter((v) => v != null);
                } else if (value) {
                  newLabelIds = [value];
                }
                this.setState({ labelIds: newLabelIds });
              }}
              placeholder="Any label"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {labels.map((label) => {
                const textColor = label.is_dark_text ? 'black' : 'white';
                return (
                  <Option key={label.id} value={label.id} label={label.name}>
                    <Tag
                      color={label.color}
                      style={{ color: textColor, marginRight: 8 }}
                    >
                      {label.name}
                    </Tag>
                  </Option>
                );
              })}
            </Select>
            {labelIds.length > 1 && (
              <div style={{
                marginTop: 8, display: 'flex', alignItems: 'center', gap: 8,
              }}
              >
                <span style={{ color: '#666' }}>Match:</span>
                <Select
                  value={labelMatchMode}
                  onChange={(value) => this.setState({ labelMatchMode: value })}
                  style={{ width: 280 }}
                  size="small"
                >
                  <Option value="any">
                    <strong>any</strong>
                    {' '}
                    <span style={{ color: '#888' }}>(document has any label)</span>
                  </Option>
                  <Option value="all">
                    <strong>all</strong>
                    {' '}
                    <span style={{ color: '#888' }}>(document has all labels)</span>
                  </Option>
                </Select>
              </div>
            )}
          </Form.Item>

          <Form.Item label="Fax Line" help="Match faxes received on this line">
            <Select
              value={faxLineId}
              onChange={(value) => this.setState({ faxLineId: value || undefined })}
              placeholder="Any fax line"
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {faxLines.map((line) => (
                <Option key={line.id} value={line.id}>
                  {`${line.number} (${line.ownerName})`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Sender Fax Number" help="Exact match, e.g. +15551234567">
            <Input
              value={sender}
              onChange={(e) => this.setState({ sender: e.target.value })}
              placeholder="Any sender"
            />
          </Form.Item>

          <Form.Item label="Sender Contact" help="Match faxes from this saved contact">
            <Select
              value={addresseeId}
              onChange={(value) => this.setState({ addresseeId: value || undefined })}
              onSearch={onSearchAddressees}
              filterOption={false}
              placeholder="Any contact"
              showSearch
              allowClear
              notFoundContent={null}
            >
              {/* Keep the saved addressee visible even when it's not in the
                  current search results. */}
              {editingAddressee
                && !addresseeOptions.some((r) => r.id === editingAddressee.id)
                && (
                <Option key={editingAddressee.id} value={editingAddressee.id}>
                  {editingAddressee.contact_name || editingAddressee.contact}
                </Option>
                )}
              {addresseeOptions.map((recipient) => (
                <Option key={recipient.id} value={recipient.id}>
                  {recipient.contact_name
                    ? `${recipient.contact_name} (${recipient.contact})`
                    : recipient.contact}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Active">
            <Switch
              checked={active}
              onChange={(checked) => this.setState({ active: checked })}
            />
            <span style={{ marginLeft: 8 }}>
              {active ? 'Rule is active' : 'Rule is inactive'}
            </span>
          </Form.Item>
        </Form>
      </Modal>
    );
  }
}

AddTeamRoutingRuleModal.defaultProps = {
  editingRule: null,
  teams: [],
  categories: [],
  labels: [],
  faxLines: [],
  addresseeOptions: [],
  onSearchAddressees: () => {},
  isLoading: false,
};

AddTeamRoutingRuleModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingRule: PropTypes.object,
  teams: PropTypes.array,
  categories: PropTypes.array,
  labels: PropTypes.array,
  faxLines: PropTypes.array,
  addresseeOptions: PropTypes.array,
  onSearchAddressees: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default AddTeamRoutingRuleModal;
